import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  KeyRound,
  Loader2,
  MessageSquare,
  ShieldCheck,
  User,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ActiveLicenseModulesList from "@/components/ActiveLicenseModulesList";
import BuyerProfileEditForm from "@/components/BuyerProfileEditForm";
import BuyerProfileSummary from "@/components/BuyerProfileSummary";
import LicenseCommercialSection from "@/components/LicenseCommercialSection";
import LicenseRenewalExpansionPanel from "@/components/LicenseRenewalExpansionPanel";
import LicenseRequestHistory from "@/components/LicenseRequestHistory";
import SecurityRecoveryAccountCard from "@/components/SecurityRecoveryAccountCard";
import ChatInternoEquipoPanel from "@/components/equipo/ChatInternoEquipoPanel";
import EquipoBasicoUsuarios from "@/pages/equipo/EquipoBasicoUsuarios";
import { formatLicenseDateForDisplay } from "@/lib/licenseDates";
import {
  hasActiveCommercialLicense,
  resolveActiveModuleIds,
} from "@/lib/commercialLicenseRequests";
import api from "@/lib/api";
import {
  computeClientSlotStats,
  computeUserSlotStats,
} from "@/lib/workspaceTeamLimits";
import { EXPANSION_KIND_USER_LIMIT } from "@/lib/commercialLicenseExpansion";
import { LICENSE_RENEWAL_EXPANSION_QUERY, LICENSE_REQUEST_HISTORY_QUERY, parseRenovacionAmpliacionPrefill } from "@/modules/config/configuracionRoutes";

function InfoCell({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono text-[11px] break-all" : "text-xs"}`}>
        {value}
      </div>
    </div>
  );
}

/**
 * Vista «Mi Negocio» limpia para comprador SaaS (sin bloques técnicos).
 */
export default function BuyerMiNegocioView({
  info,
  prof,
  setProf,
  isOwner,
  saveProfile,
  busyProf,
  secRows,
  setSecRows,
  onClearSecurityRecovery,
  user,
  lic,
  licPayload,
  statusLabel,
  statusColor,
  moduleCatalog,
  onLicenseInstalled,
  canShowCommercial,
  suppressDesignerLicenseCtas,
  buyerPreviewMode = false,
}) {
  const [editTitularOpen, setEditTitularOpen] = useState(false);
  const [editFiscalOpen, setEditFiscalOpen] = useState(false);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [renewalKickPrefill, setRenewalKickPrefill] = useState(null);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [activeClientsCount, setActiveClientsCount] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const renewalDeepLink = useMemo(() => parseRenovacionAmpliacionPrefill(searchParams), [searchParams]);
  const clearRenewalDeepLink = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(LICENSE_RENEWAL_EXPANSION_QUERY);
    next.delete(LICENSE_REQUEST_HISTORY_QUERY);
    next.delete("expansion_type");
    next.delete("expansion_module");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /** Secciones plegables: siempre arrancan cerradas al montar (salir/entrar a Configuración). */
  const [sectionsOpen, setSectionsOpen] = useState(() => {
    const open = [];
    if (renewalDeepLink.openHistory) open.push("history");
    if (renewalDeepLink.scrollToRenewal || renewalDeepLink.prefill) open.push("license");
    return open;
  });

  useEffect(() => {
    if (renewalDeepLink.openHistory) {
      setSectionsOpen((prev) => (prev.includes("history") ? prev : [...prev, "history"]));
    }
  }, [renewalDeepLink.openHistory]);

  useEffect(() => {
    if (!renewalDeepLink.scrollToRenewal && !renewalDeepLink.prefill) return;
    setSectionsOpen((prev) => (prev.includes("license") ? prev : [...prev, "license"]));
  }, [renewalDeepLink.scrollToRenewal, renewalDeepLink.prefill]);

  useEffect(() => {
    if (!renewalDeepLink.scrollToRenewal && !renewalDeepLink.prefill) return;
    const timer = window.setTimeout(() => {
      document.getElementById("buyer-license-renewal")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [renewalDeepLink.scrollToRenewal, renewalDeepLink.prefill]);

  const loadWorkspaceStats = useCallback(async () => {
    if (!isOwner) return;
    try {
      const [usersRes, clientsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/clients"),
      ]);
      setWorkspaceUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setActiveClientsCount(Array.isArray(clientsRes.data) ? clientsRes.data.length : 0);
    } catch {
      setWorkspaceUsers([]);
      setActiveClientsCount(null);
    }
  }, [isOwner]);

  useEffect(() => {
    loadWorkspaceStats();
  }, [loadWorkspaceStats]);

  useEffect(() => {
    if (!usersModalOpen) {
      loadWorkspaceStats();
    }
  }, [usersModalOpen, loadWorkspaceStats]);

  const openUsersModal = useCallback(() => setUsersModalOpen(true), []);
  const openChatModal = useCallback(() => setChatModalOpen(true), []);
  const openUsersFromChat = useCallback(() => {
    setChatModalOpen(false);
    setUsersModalOpen(true);
  }, []);
  const handleRequestLicenseExpansion = useCallback(() => {
    setUsersModalOpen(false);
    setSectionsOpen((prev) => (prev.includes("license") ? prev : [...prev, "license"]));
    setRenewalKickPrefill({ expansion_type: EXPANSION_KIND_USER_LIMIT, requested_modules: {} });
    window.setTimeout(() => {
      document.getElementById("buyer-license-renewal")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  }, []);

  const sub = info?.subscription || {};
  const trialEndsLabel = sub?.trial_ends_at ? String(sub.trial_ends_at).slice(0, 10) : "";
  const trialDays = Number(info?.trial_days) > 0 ? Number(info.trial_days) : 3;
  const inTrial = sub?.mode === "trial";
  const trialActive = inTrial && sub?.ok;
  const hasActiveLicense = hasActiveCommercialLicense(info);
  const activeModuleIds = hasActiveLicense ? resolveActiveModuleIds(info) : [];
  const authUsers = info?.license?.authorized_users ?? licPayload?.authorized_users;
  const authClients = info?.license?.authorized_clients ?? licPayload?.authorized_clients;
  const userSlots = useMemo(() => computeUserSlotStats(info, workspaceUsers), [info, workspaceUsers]);
  const clientSlots = useMemo(
    () => computeClientSlotStats(info, activeClientsCount),
    [info, activeClientsCount],
  );

  const titularDisplay =
    (prof.razon_social || "").trim()
    || (licPayload.licensee_name || "").trim()
    || (prof.nombre_comercial || "").trim()
    || (prof.responsable || "").trim()
    || "—";

  const statusMessage = useMemo(() => {
    if (hasActiveLicense && ["active", "expiring_soon"].includes(String(lic?.status || ""))) {
      if (activeModuleIds.length >= 4) {
        return "Tu licencia está activa e incluye todos los módulos principales autorizados.";
      }
      return "Tu licencia está activa. Puedes usar los módulos autorizados hasta la fecha de vencimiento indicada.";
    }
    if (trialActive) {
      return `Estás usando una prueba. Para continuar después del periodo de prueba, solicita tu licencia.`;
    }
    if (inTrial && !sub?.ok) {
      return "Tu periodo de prueba terminó. Solicita una licencia para seguir usando el sistema.";
    }
    return "Aún no tienes licencia formal activa. Puedes enviar una solicitud a BNT.";
  }, [hasActiveLicense, lic?.status, activeModuleIds.length, trialActive, inTrial, sub?.ok]);

  const showCommercialSection =
    Boolean(canShowCommercial && info && (!suppressDesignerLicenseCtas || buyerPreviewMode));
  const showRenewalPanel = Boolean(info) && hasActiveLicense;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="buyer-mi-negocio-view">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-[#0F2D5C] m-0">Mi Negocio</h2>
        <p className="text-sm text-slate-600 m-0">
          Consulta tu licencia, módulos activos y datos principales de tu cuenta.
        </p>
      </header>

      {sub?.ok === false && sub?.reason ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold m-0">Acceso limitado</p>
          <p className="text-xs mt-1 mb-0">
            {sub.reason === "trial_expired"
              ? `La prueba gratuita (${trialDays} días) terminó. Solicita licencia para continuar.`
              : "Instala o renueva tu licencia para recuperar el uso completo."}
          </p>
        </div>
      ) : null}

      {trialActive && trialEndsLabel ? (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs text-blue-900">
          <span className="font-semibold">Periodo de prueba activo</span>
          {" — "}hasta el {trialEndsLabel}.
        </div>
      ) : null}

      <p className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 m-0">
        {statusMessage}
      </p>

      <div className="grid gap-4 lg:grid-cols-3">
        <section
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-1"
          data-testid="buyer-license-current-card"
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-900 m-0 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Licencia actual
            </h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-md border font-semibold ${statusColor}`}>
              {statusLabel}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-2 text-xs">
            <InfoCell label="Titular" value={titularDisplay} />
            <InfoCell label="RFC" value={licPayload.licensee_rfc || prof.rfc || "—"} mono />
            <InfoCell label="Plan" value={licPayload.plan || "—"} />
            <InfoCell
              label="Vigencia desde"
              value={
                licPayload.valid_from
                  ? formatLicenseDateForDisplay(String(licPayload.valid_from).slice(0, 10))
                  : "—"
              }
            />
            <InfoCell
              label="Vigencia hasta"
              value={
                licPayload.valid_to
                  ? formatLicenseDateForDisplay(String(licPayload.valid_to).slice(0, 10))
                  : "—"
              }
            />
            <InfoCell
              label="ID licencia"
              value={(licPayload.license_id || "").slice(0, 8) || "—"}
              mono
            />
            <InfoCell label="ID instalación" value={info?.installation_id || "—"} mono />
          </dl>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-1">
          <h3 className="text-sm font-semibold text-slate-900 m-0 mb-2 flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Módulos activos
          </h3>
          {hasActiveLicense ? (
            <ActiveLicenseModulesList systemInfo={info} moduleCatalog={moduleCatalog} />
          ) : (
            <p className="text-xs text-slate-500 m-0">
              {trialActive
                ? "Durante la prueba puedes usar los módulos del trial. Solicita licencia formal para fijar tu contrato."
                : "Sin módulos de licencia formal activa."}
            </p>
          )}
        </section>

        <section
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-1"
          data-testid="buyer-license-scope-card"
        >
          <h3 className="text-sm font-semibold text-slate-900 m-0 mb-2 flex items-center gap-2">
            <Users className="w-4 h-4" /> Usuarios y clientes
          </h3>
          <dl className="space-y-2 text-xs m-0">
            <div className="grid grid-cols-3 gap-2 pb-2 border-b border-slate-100">
              <InfoCell label="Usuarios autorizados" value={userSlots.authorized ?? authUsers ?? "—"} />
              <InfoCell label="Activos" value={userSlots.active ?? "—"} />
              <InfoCell label="Disponibles" value={userSlots.available ?? "—"} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <InfoCell label="Clientes autorizados" value={clientSlots.authorized ?? authClients ?? "—"} />
              <InfoCell label="Activos" value={clientSlots.active ?? "—"} />
              <InfoCell label="Disponibles" value={clientSlots.available ?? "—"} />
            </div>
          </dl>
          {isOwner ? (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                data-testid="buyer-admin-users-btn"
                onClick={openUsersModal}
              >
                Administrar usuarios
              </Button>
              {userSlots.atLimit ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleRequestLicenseExpansion}
                >
                  Solicitar más auxiliares
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                data-testid="buyer-chat-interno-btn"
                onClick={openChatModal}
              >
                <MessageSquare className="w-3.5 h-3.5 mr-1" />
                Chat del equipo
              </Button>
            </div>
          ) : null}
          {hasActiveLicense ? (
            <p className="text-[10px] text-slate-500 mt-3 mb-0">
              Para ampliar usuarios, clientes o renovar vigencia, solicita una actualización a BNT.
            </p>
          ) : null}
        </section>
      </div>

      <Accordion
        type="multiple"
        className="space-y-2"
        value={sectionsOpen}
        onValueChange={setSectionsOpen}
        data-testid="buyer-mi-negocio-sections-accordion"
      >
        <AccordionItem
          value="titular"
          className="border border-slate-200 rounded-lg px-4 bg-white"
          data-testid="buyer-section-titular"
        >
          <AccordionTrigger
            className="text-sm font-semibold hover:no-underline py-3"
            data-testid="buyer-section-titular-trigger"
          >
            <span className="flex items-center gap-2">
              <User className="w-4 h-4 shrink-0" /> Datos del titular
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
              <p className="text-[11px] text-slate-500 m-0">
                Para cambiar estos datos, usa el formulario de edición y guarda.
              </p>
              {isOwner ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setEditTitularOpen((v) => !v)}
                  data-testid="buyer-edit-titular-toggle"
                >
                  {editTitularOpen ? "Ocultar formulario" : "Editar datos del titular"}
                  <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${editTitularOpen ? "rotate-180" : ""}`} />
                </Button>
              ) : null}
            </div>
            {!editTitularOpen ? (
              <BuyerProfileSummary profile={prof} systemInfo={info} testId="buyer-titular-summary" />
            ) : (
              <div className="space-y-3">
                <BuyerProfileEditForm prof={prof} setProf={setProf} disabled={!isOwner || busyProf} />
                {isOwner ? (
                  <div className="flex justify-end">
                    <Button
                      onClick={saveProfile}
                      disabled={busyProf}
                      className="h-9 bg-[#0F2D5C] hover:bg-[#0a1f42]"
                      data-testid="buyer-save-profile-btn"
                    >
                      {busyProf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Guardar datos
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="facturacion"
          className="border border-slate-200 rounded-lg px-4 bg-white"
          data-testid="buyer-section-facturacion"
        >
          <AccordionTrigger
            className="text-sm font-semibold hover:no-underline py-3"
            data-testid="buyer-section-facturacion-trigger"
          >
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4 shrink-0" /> Facturación
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="flex flex-wrap items-start justify-end gap-2 mb-3">
              {isOwner ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setEditFiscalOpen((v) => !v)}
                  data-testid="buyer-edit-fiscal-toggle"
                >
                  {editFiscalOpen ? "Ocultar" : "Editar datos fiscales"}
                </Button>
              ) : null}
            </div>
            {!editFiscalOpen ? (
              <dl className="grid sm:grid-cols-2 gap-2 text-xs m-0">
                <InfoCell label="Razón social" value={prof.razon_social || "—"} />
                <InfoCell label="RFC" value={prof.rfc || "—"} mono />
                <InfoCell label="Régimen fiscal" value={prof.regimen_fiscal || "—"} />
                <InfoCell label="C.P." value={prof.codigo_postal || "—"} mono />
                <InfoCell label="Domicilio fiscal" value={prof.domicilio || "—"} />
                <InfoCell label="Correo facturación" value={prof.correo_facturacion || prof.email || "—"} />
              </dl>
            ) : (
              <div className="space-y-3">
                <BuyerProfileEditForm prof={prof} setProf={setProf} disabled={!isOwner || busyProf} />
                {isOwner ? (
                  <div className="flex justify-end">
                    <Button onClick={saveProfile} disabled={busyProf} className="h-9 bg-[#0F2D5C] hover:bg-[#0a1f42]">
                      {busyProf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Guardar datos fiscales
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {showRenewalPanel ? (
          <AccordionItem
            value="license"
            className="border border-slate-200 rounded-lg px-4 bg-white"
            data-testid="buyer-section-license"
          >
            <AccordionTrigger
              className="text-sm font-semibold hover:no-underline py-3"
              data-testid="buyer-section-license-trigger"
            >
              <span className="flex items-center gap-2 uppercase tracking-wide text-[#0F2D5C]">
                <KeyRound className="w-4 h-4 shrink-0" /> Licencia y ampliaciones
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-4" id="buyer-license-renewal">
              <LicenseRenewalExpansionPanel
                systemInfo={info}
                profileDefaults={prof}
                previewMode={buyerPreviewMode}
                prefill={renewalDeepLink.prefill || renewalKickPrefill}
                showHistory={false}
                modalFlow
                embedded
                onFormClose={clearRenewalDeepLink}
                onPrefillConsumed={() => {
                  setRenewalKickPrefill(null);
                  clearRenewalDeepLink();
                }}
              />
            </AccordionContent>
          </AccordionItem>
        ) : null}

        {showCommercialSection && !hasActiveLicense ? (
          <AccordionItem
            value="commercial"
            className="border border-slate-200 rounded-lg px-4 bg-white"
            data-testid="buyer-section-commercial"
          >
            <AccordionTrigger
              className="text-sm font-semibold hover:no-underline py-3"
              data-testid="buyer-section-commercial-trigger"
            >
              Solicitud de licencia
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <LicenseCommercialSection
                systemInfo={info}
                profileDefaults={prof}
                onLicenseInstalled={onLicenseInstalled}
                buyerCleanLayout
              />
            </AccordionContent>
          </AccordionItem>
        ) : null}

        <AccordionItem value="history" className="border border-slate-200 rounded-lg px-4 bg-white">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
            Historial de solicitudes
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <LicenseRequestHistory enabled={canShowCommercial} />
          </AccordionContent>
        </AccordionItem>

        {isOwner ? (
          <AccordionItem value="security" className="border border-slate-200 rounded-lg px-4 bg-white">
            <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
              Seguridad de la cuenta
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <SecurityRecoveryAccountCard
                configured={Boolean(info?.security_recovery?.configured)}
                questionRows={secRows}
                onChangeRows={setSecRows}
                onSave={saveProfile}
                onClear={onClearSecurityRecovery}
                busy={busyProf}
              />
            </AccordionContent>
          </AccordionItem>
        ) : null}
      </Accordion>

      <Dialog open={usersModalOpen} onOpenChange={setUsersModalOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm"
          data-testid="buyer-team-users-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-base text-[#0F2D5C]">Usuarios operativos</DialogTitle>
          </DialogHeader>
          <EquipoBasicoUsuarios embedded onRequestLicenseExpansion={handleRequestLicenseExpansion} />
        </DialogContent>
      </Dialog>

      <Dialog open={chatModalOpen} onOpenChange={setChatModalOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm"
          data-testid="buyer-team-chat-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-base text-[#0F2D5C]">Chat del equipo</DialogTitle>
          </DialogHeader>
          <ChatInternoEquipoPanel embedded onOpenUsersManage={openUsersFromChat} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
